import { AppInsights } from '@/appInsights';
import { AuthorizedUser } from '@/models/authorized-user';
import { CMPApi } from '@/modules/cmp-api';
import { CommitteeContactDetail, EmploymentDetail, EoiFormView, MemberDetail, PersonalDetail } from '@/modules/typescript-cmpApi-client/models';
import store from '@/store';
import { EventEmitter } from 'events';
import { Options, Vue } from 'vue-class-component';
import { Inject } from 'vue-property-decorator';
import EoiWording from '../../assets/config/eoi-wording.json';
import LoadingSpinner from '../loading-spinner/loading-spinner';

@Options({
  props: {
    eoi: {},
    isReviewing: Boolean,
  },
  emits: ['update:eoi'],
  components: {
    LoadingSpinner,
  }
})
export default class Review extends Vue {
  eoi!: EoiFormView;
  isReviewing!: boolean;

  @Inject({ from: 'apiService', default: new CMPApi() })
  private apiService!: CMPApi;

  @Inject({ from: 'eventController', default: new EventEmitter })
  private eventCtrl!: EventEmitter;

  private ai = new AppInsights;

  private memberDetail: MemberDetail = {};
  private employmentDetail: EmploymentDetail = {};
  private personalDetail: PersonalDetail = {};
  private committeeContactDetail: CommitteeContactDetail = {};
  private address: any;
  private isPcHolder = false;
  private isLoadingData = false;

  private emailPreferencesList: any = [];
  private areaOfInterestsList: any = [];

  private initialEmailPreferencesList: any = [];
  private initialAreaOfInterestsList: any = [];
  private lawId = '';

  private agreeCheck = false;
  private committeeYear = '';

  get pageHeader(): string {
    return EoiWording[0].review['pageHeader']
  }


  get userAccount(): AuthorizedUser {
    return store.getters.getUserAccount;
  }

  get getConfirmationText() {
    let text = 'Yes, all details are correct';
    if (this.eoi !== []) {
      const _personalDetails = this.eoi.confirmPersonalDetails;
      const _employeeDetails = this.eoi.confirmEmployeeDetails;
      const _committeeContactDetails = this.eoi.confirmCommitteeContactDetails;
      if (!this.personalDetail.isPcHolder) {
        if (!_personalDetails || !_employeeDetails) {
          text = "No";
        }
      } else {
        if (!_personalDetails || !_employeeDetails) {
          text = "No, my Personal or Employment details are not correct";
        }
      }
      return text;
    }
    else {
      return '';
    }
  }

  mounted() {
    if (this.eoi == null) {
      this.eventCtrl.emit('show-snackbar', 'error', 'Error on loading EOI from data');
      this.ai.trackException('Error getting details info: ' + 'Error on loading EOI from data');
      this.eventCtrl.emit('isLoadingData', false);
      this.$router.push('/dashboard');
    } else {
      window.sessionStorage.setItem('agreeCheck', '0');
      this.isPcHolder = this.userAccount.user.isPcHolder || false;
      this.getMyMemberDetails();
      this.eventCtrl.emit('canSubmit', false);
    }

  }

  private getPhoneNumber() {
    return this.committeeContactDetail.phone.phoneAreaCode + ' ' + this.committeeContactDetail.phone.phoneNumber;
  }

  private setAgreeCheck() {
    window.sessionStorage.setItem('agreeCheck', this.agreeCheck ? 'true' : 'false');
    this.eoi.acceptPersonalInfoCollectNotice = (this.agreeCheck);
    this.$emit('update:eoi', this.eoi);
    store.dispatch('setEoiForm', this.eoi);
    this.eventCtrl.emit('canSubmit', this.agreeCheck);
  }

  private getAddressEncoded() {
    let address = this.committeeContactDetail.address;
    const countryName: string = address['countryName'];
    let addressEncoded = '';
    address = JSON.parse(JSON.stringify(address).replace(/:null/gi, ':""'));  // replace all null value
    if (countryName.toLowerCase() == 'australia') {
      switch (address['addressTypeId']) {
        case 1: // Street
          addressEncoded = address['buildingName'] + '\n';
          addressEncoded += address['buildingTypeName'] + ' ' + address['buildingNumber'] + ' ' + address['level'] + '\n';
          addressEncoded += address['streetNumber'] + ' ' + address['streetName'] + ' ' + address['streetTypeName'] + '\n';
          addressEncoded += address['suburbName'] + ', ' + address['stateName'] + ' ' + address['postcode'];
          break;
        case 2: // PO Box
          addressEncoded = address['deliveryTypeName'] + ' ' + address['poBox'] + '\n';
          addressEncoded += address['suburbName'] + ', ' + address['stateName'] + ' ' + address['postcode'];
          break;
        // case 3: // DX address
        //   addressEncoded = 'DX ' + address['dxNumber'] + '\n';
        //   addressEncoded += address['dxExchangeTypeName'] + '\n';
        //   break;
      }
    } else {
      addressEncoded = this.committeeContactDetail.address['foreignAddress'] + '\n' + this.committeeContactDetail.address['countryName'];
    }
    return addressEncoded.trim();
  }

  private getMyMemberDetails() {
    this.eventCtrl.emit('isLoadingData', true);
    this.apiService.memberApi?.apiV1MemberGetMyMemberDetailGet()
      .then((res: any) => {
        store.dispatch('setMyMemberDetails', res.data.data);
        this.personalDetail = res.data.data.personalDetail;        
        this.lawId = this.personalDetail.lawId;
        this.GetAreaOfInterests();
        this.GetEmailPreferences();
        this.employmentDetail = res.data.data.employmentDetail;
        this.committeeContactDetail = res.data.data.committeeContactDetail;
        this.address = this.committeeContactDetail.address;
        this.committeeYear = this.eoi.year.committeeYear;
      })
      .catch((err: any) => {
        console.log('getMyMemberDetails: ', err);
        const errorMsg = err.response ? err.response.status : err.toString();
        this.eventCtrl.emit('show-snackbar', 'error', 'Error getting My Member Details: ' + errorMsg);
        this.ai.trackException('Error getting details info: ' + err);
        this.eventCtrl.emit('isLoadingData', false);
      })
  }

  private GetAreaOfInterests() {
    if(this.$route.name == "EoiReview") {
      this.initialAreaOfInterestsList = [];
      this.apiService.memberApi?.apiV1GetIndividualAreasOfInterestLawIdGet(this.lawId)
      .then((res: any) => {
        this.initialAreaOfInterestsList = res.data.data;
      })
      .catch((err: any) => {
        console.log(err);
        this.eventCtrl.emit('show-snackbar', 'error', 'Error getting Areas of Interests: ' + err.response.status);
        this.ai.trackException('Error getting Areas of Interests: ' + err);
      })
    }
    else {
      this.areaOfInterestsList = store.getters.getAreaOfInterests;
      
      if (this.areaOfInterestsList == null) {
        this.apiService.memberApi?.apiV1GetIndividualAreasOfInterestLawIdGet(this.lawId)
          .then((res: any) => {
            this.areaOfInterestsList = res.data.data;      
            store.dispatch('setAreaOfInterests', this.areaOfInterestsList);
            this.eoi.aoiList = this.areaOfInterestsList;
            this.initialAreaOfInterestsList = this.areaOfInterestsList;
          })
          .catch((err: any) => {
            console.log(err);
            this.eventCtrl.emit('show-snackbar', 'error', 'Error getting Areas of Interests: ' + err.response.status);
            this.ai.trackException('Error getting Areas of Interests: ' + err);
          })
      }
      else {
        this.eoi.aoiList = this.areaOfInterestsList;
        this.initialAreaOfInterestsList = this.areaOfInterestsList;
      }
    }

  }

  private GetEmailPreferences() {
    if(this.$route.name == "EoiReview") {
      this.initialEmailPreferencesList = [];
      this.apiService.memberApi?.apiV1GetIndividualEmailPreferencesLawIdGet(this.lawId)
      .then((res: any) => {
        this.initialEmailPreferencesList = res.data.data;        
        this.eventCtrl.emit('isLoadingData', false);
      })
      .catch((err: any) => {
        console.log(err);
        this.eventCtrl.emit('show-snackbar', 'error', 'Error getting Email Preferences: ' + err.response.status);
        this.ai.trackException('Error getting Email Preferences: ' + err);          
        this.eventCtrl.emit('isLoadingData', false);
      })
    }
    else {
      this.emailPreferencesList = store.getters.getEmailPreferences;

      if (this.emailPreferencesList == null) {
        this.apiService.memberApi?.apiV1GetIndividualEmailPreferencesLawIdGet(this.lawId)
          .then((res: any) => {
            this.emailPreferencesList = res.data.data;
            store.dispatch('setEmailPreferences', this.emailPreferencesList);          
            this.eventCtrl.emit('isLoadingData', false);
            this.eoi.epList = this.emailPreferencesList;
          })
          .catch((err: any) => {
            console.log(err);
            this.eventCtrl.emit('show-snackbar', 'error', 'Error getting Email Preferences: ' + err.response.status);
            this.ai.trackException('Error getting Email Preferences: ' + err);          
            this.eventCtrl.emit('isLoadingData', false);
          })
      }
      else { 
        this.eoi.epList = this.emailPreferencesList;
        this.initialEmailPreferencesList = this.emailPreferencesList;
        this.eventCtrl.emit('isLoadingData', false);
      }
    }
    
  }

}
